import React, { useState, useEffect } from 'react'

import * as d3 from 'd3'
import Decimal from 'decimal.js'

import style from './Chartv2.module.css'

function getColor(type) {
  let color
  switch (type) {
    case 'normal': {
      color = process.env.CHART_COLOR_NORMAL || 'rgb(80, 210, 194)'
      break
    }
    case 'abnormal': {
      color = process.env.CHART_COLOR_ABNORMAL || 'rgb(251, 177, 60)'
      break
    }
    default: {
      color = 'silver'
      break
    }
  }
  if (!color) color = 'silver'
  return color
}
function stringToDate(stringDate) {
  return new Date(stringDate)
}
function findMinMax(dataArray, valueKey) {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return { overallMin: undefined, overallMax: undefined }
  }

  if (typeof valueKey !== 'string' || valueKey === '') {
    console.error("Error: 'valueKey' must be a non-empty string.")
    return { overallMin: undefined, overallMax: undefined }
  }

  let overallMin = Number(dataArray[0][valueKey])
  let overallMax = Number(dataArray[0][valueKey])

  for (let i = 1; i < dataArray.length; i++) {
    const currentValue = Number(dataArray[i][valueKey])

    if (currentValue < overallMin) {
      overallMin = currentValue
    }

    if (currentValue > overallMax) {
      overallMax = currentValue
    }
  }
  return { overallMin: overallMin, overallMax: overallMax }
}

export function Chartv2({ data, ranges }) {
  const formatTime = d3.utcFormat('%b %d, %Y')

  // const { labOrder } = req.body
  const CHART_SCALE = process.env.CHART_SCALE || 100
  const CHART_SILVER_HEIGHT = process.env.CHART_SILVER_HEIGHT || 40
  const CHART_WIDTH = process.env.CHART_WIDTH || 10
  const CHART_HEIGHT = process.env.CHART_HEIGHT || 4
  const CHART_ENABLE_PLOT_LABEL = process.env.CHART_ENABLE_PLOT_LABEL || 'true'
  const CHART_FONT_SIZE = process.env.CHART_FONT_SIZE || '0.75vw'
  let rawScale = 1 * CHART_SCALE

  // SVG dimensions
  let scale = CHART_SCALE || 100
  let width = (CHART_WIDTH || 10) * scale
  let height = (CHART_HEIGHT || 4) * scale
  const margin = { top: 20, right: 20, bottom: 30, left: 50 }
  let chart_height = height

  const results = data

  if (!data) return <></>
  if (!ranges) return <></>

  // Compute for the ranges or type of chart first using ranges
  const chart_divider = []
  // set the silver lining first
  const silver_height = new Decimal(CHART_SILVER_HEIGHT || 40).toNumber()
  chart_height = chart_height - silver_height
  chart_divider.push({
    transform: `translate(0, ${chart_height})`,
    fill: 'silver',
    start: height - chart_height,
    height: height - chart_height,
  })
  var t_lastvalue
  ranges.map((range) => {
    var d_diff = Decimal.sub(range.max, range.min).abs()
    var d_height_wo_silver = Decimal.sub(height, silver_height).abs()
    t_lastvalue = Decimal.mul(d_height_wo_silver, d_diff).toNumber()

    chart_height = chart_height - t_lastvalue
    chart_divider.push({
      transform: `translate(0, ${chart_height})`,
      fill: getColor(range.type),
      start: height - chart_height,
      height: t_lastvalue,
    })
  })

  // Create an SVG element
  var svg = d3.create('svg').attr('width', width).attr('height', height).append('g')

  const g = svg.append('g')

  g.selectAll('line')
    .data(chart_divider)
    .enter()
    .append('line')
    .attr('x1', '0%')
    .attr('x2', '100%')
    .attr('y1', (d) => height - d.start)
    .attr('y2', (d) => height - d.start)
    .attr('stroke-width', 1)
    .attr('stroke', 'rgba(0,0,0,0.1)')

  const chart_plots = []
  const cx_space = (width - (margin.left + margin.right)) / (results.length + 1)
  let cx_space_total = margin.left
  let chart_height_2 = height - silver_height
  // Check first if range is a 2-part chart (normal-abnormal or abnormal-normal)
  // or 3-part (abnormal-normal-abnormal)
  if (ranges.length == 2) {
    // let mean_chart = 0;

    // get the orverall min and max results for mid point fallback
    const results_minmax = findMinMax(results, 'tooltip')
    for (let counter in results) {
      let result_ins = results[counter]
      var mid_ref = new Decimal(result_ins?.ref_range?.replace(/[^0-9]/g, '') ?? 0).toNumber()
      if (!mid_ref) {
        // if no mid_ref, that means the ref_range from the lab doesn't make sense mathematically
        // hence, creating a mid_ref or mid point based on the result data
        mid_ref = results_minmax.overallMax + results_minmax.overallMax / 2 - (results_minmax.overallMin - results_minmax.overallMin / 2)
      }
      var max_ref = mid_ref + mid_ref
      var min_ref = mid_ref - mid_ref
      var total_ref = max_ref - min_ref
      var new_result = new Decimal(result_ins.tooltip ?? 0).toNumber() / total_ref
      results[counter].value = new_result
    }
    // mean_chart = mean_chart / results.length;
  }
  for (let i_result2 in results) {
    let result_ins = results[i_result2]
    cx_space_total += cx_space
    chart_plots.push({
      cx: cx_space_total,
      cy: chart_height_2 * (1 - result_ins.value),
      color: getColor(result_ins.type),
      result_date: formatTime(stringToDate(result_ins.date)),
      label: result_ins.tooltip,
    })
  }

  svg
    .selectAll('circle')
    .data(chart_plots)
    .enter()
    .append('circle')
    .attr('cx', (d) => d.cx)
    .attr('cy', (d) => d.cy)
    .attr('r', 5)
    .attr('stroke-width', 4)
    .attr('stroke', (d) => d.color)
    .attr('fill', 'white')

  // add label to plots
  if (CHART_ENABLE_PLOT_LABEL == 'true') {
    svg
      .selectAll('text')
      .data(chart_plots)
      .enter()
      .append('text')
      .attr('transform', (d) => `translate(${d.cx + 18}, ${d.cy - 5})`)
      .attr('font-size', CHART_FONT_SIZE || '1vw')
      .attr('font-family', 'sans-serif')
      .attr('text-anchor', 'middle')
      .text((d) => d.label)
  }

  const xTicks = g
    .append('g')
    .attr('transform', 'translate(0, 157)')
    .attr('fill', 'none')
    .attr('font-size', 10)
    .attr('font-family', 'sans-serif')
    .attr('text-anchor', 'middle')
    .selectAll('g')
    .data(chart_plots)
    .enter()
    .append('g')
    .attr('class', 'tick')
    .attr('opacity', 1)
    .attr('transform', (d) => `translate(${d.cx}, 0)`)

  xTicks
    .append('text')
    .attr('fill', 'currentColor')
    .attr('y', height * 0.5 + silver_height * 0.25)
    .attr('dy', '1em')
    .attr('font-size', CHART_FONT_SIZE || '1vw')
    .append('tspan')
    .attr('x', 0)
    .attr('dy', '1em')
    .text((d) => d.result_date)

  // Add X axis --> it is a date format
  var x = d3.scaleLinear().domain([0, width]).range([0, width])
  svg
    .append('g')
    .attr('transform', 'translate(0,' + height + ')')
    .call(d3.axisBottom(x))
  // Add Y axis
  var y = d3.scaleLinear().domain([0, height]).range([0, height])
  svg.append('g').call(d3.axisLeft(y))

  // Add the line
  svg
    .append('path')
    .datum(chart_plots)
    .attr('fill', 'transparent')
    .attr('stroke', '#b3b2d2')
    .attr('stroke-width', 1)
    .attr(
      'd',
      d3
        .line()
        .curve(d3.curveBumpX) // Just add that to have a curve instead of segments
        .x(function (d) {
          return x(d.cx)
        })
        .y((d) => d.cy),
    )

  const yTicks = g
    .append('g')
    .attr('fill', 'none')
    .attr('font-size', 10)
    .attr('font-family', 'sans-serif')
    .attr('text-anchor', 'start')
    .selectAll('g')
    .data(chart_divider)
    .enter()
    .append('g')
    .attr('class', 'tick')
    .attr('opacity', 1)
    .attr('transform', (d) => d.transform)
    .attr('fill', (d) => d.fill)

  yTicks
    .append('rect')
    .attr('width', 10) // widthe of each color on y-axis
    .attr('height', (d, i) => d.height) // height of each color on y-axis

  // return <div className="chartv2" ></div>
  return (
    <div className="chartv2">
      <svg xmlns="http://www.w3.org/2000/svg" height={height} width={width}>
        <g dangerouslySetInnerHTML={{ __html: svg.html() }}></g>
      </svg>
    </div>
  )
}
/* eslint-enable */
